// SYNTHETIC DEMO DATA ONLY. Every person, phone number, email and ID here is invented.
// Phones use the +91 55500 series (not a mobile prefix); emails use example.com (never delivered).
// The calling job must never dial records marked synthetic.
import type {
  Agent,
  AuditEntry,
  Baby,
  Call,
  CallOutcome,
  AgentType,
  DailyStats,
  Dose,
  DoseStatus,
  Facility,
  Flag,
  Mother,
  PlatformAlert,
  ProtocolStep,
  Stage,
  SupportAccessRequest,
  User,
  Booking,
  SlotSettings,
} from '../types';
import type { ErrorLogEntry, ServiceHealth } from '../types';
import { VACCINE_CODES } from './codes';
import demoConversations from './demoConversations.json';
import { addDays, dayDiff, daysAgoAt, minutesAgo, parseDate, startOfToday, statusByDiff, toIsoDate } from '../lib/dates';

const today = startOfToday();
const dateOffset = (n: number) => toIsoDate(addDays(today, n));

export const facilities: Facility[] = [
  {
    id: 'sharma-meerut',
    legalName: 'Sharma Hospital & Maternity Centre Pvt. Ltd.',
    displayNameEn: 'Sharma Hospital',
    displayNameHi: 'शर्मा हॉस्पिटल',
    shortName: 'Sharma Hospital',
    logoUrl: '/logos/sharma-hospital.svg',
    brandColor: '#C2410C',
    address: 'Garh Road, near Medical College',
    city: 'Meerut',
    state: 'Uttar Pradesh',
    pin: '250004',
    mainPhone: '+91 55500 00001',
    outboundNumber: '+91 55500 00002',
    whatsappNumber: '+91 55500 00003',
    languages: ['hi', 'en'],
    voice: 'female',
    callingHours: { start: '10:00', end: '19:00' },
    retryRule: '1 retry next day',
    services: { baby: true, pregnancy: true },
    baselineOnTimeRate: 62,
    doctors: [
      { title: 'Dr', name: 'Anita Sharma', specialty: 'gynaecologist' },
      { title: 'Dr', name: 'Rajesh Kumar', specialty: 'paediatrician' },
    ],
    escalationContact: { name: 'Dr Anita Sharma', phone: '+91 55500 00004' },
    outOfHoursPlan: 'Call the hospital emergency line; the on-call doctor responds within 30 minutes.',
    outOfHoursContact: { name: 'Hospital emergency desk', phone: '+91 55500 00005' },
    numberMode: 'own',
    goLive: { script: true, consent: true, number: true, testCalls: true, escalation: true },
    status: 'active',
    hfrId: 'DEMO-HFR-0001',
  },
  {
    id: 'gupta-haridwar',
    legalName: 'Gupta Maternity & Child Clinic',
    displayNameEn: 'Gupta Maternity & Child Clinic',
    displayNameHi: 'गुप्ता मैटरनिटी एवं चाइल्ड क्लिनिक',
    shortName: 'Gupta Clinic',
    logoUrl: '/logos/gupta-clinic.svg',
    brandColor: '#4338CA',
    address: 'Ranipur More, Delhi–Haridwar Road',
    city: 'Haridwar',
    state: 'Uttarakhand',
    pin: '249401',
    mainPhone: '+91 55500 00006',
    outboundNumber: '+91 55500 00007',
    whatsappNumber: '+91 55500 00008',
    languages: ['hi'],
    voice: 'female',
    callingHours: { start: '10:00', end: '19:00' },
    retryRule: '1 retry next day',
    services: { baby: true, pregnancy: true },
    baselineOnTimeRate: 58,
    doctors: [
      { title: 'Dr', name: 'Shalini Gupta', specialty: 'gynaecologist' },
      { title: 'Dr', name: 'Manoj Gupta', specialty: 'paediatrician' },
    ],
    escalationContact: { name: 'Dr Shalini Gupta', phone: '+91 55500 00009' },
    outOfHoursPlan: 'Go to District Hospital Haridwar emergency; clinic calls back next morning.',
    outOfHoursContact: { name: 'District Hospital Haridwar', phone: '+91 55500 00010' },
    numberMode: 'platform',
    goLive: { script: true, consent: true, number: true, testCalls: true, escalation: true },
    status: 'active',
  },
];

export const users: User[] = [
  { id: 'u-super', facilityId: null, name: 'Rohit Mehta', email: 'superadmin@example.com', phone: '+91 55500 00011', role: 'super_admin', active: true },
  { id: 'u-s-admin', facilityId: 'sharma-meerut', name: 'Vikas Sharma', email: 'sharma.admin@example.com', phone: '+91 55500 00012', role: 'facility_admin', active: true },
  { id: 'u-s-doc', facilityId: 'sharma-meerut', name: 'Dr Anita Sharma', email: 'sharma.doctor@example.com', phone: '+91 55500 00004', role: 'doctor', active: true, title: 'Dr', specialty: 'gynaecologist', doctor: { qualification: 'MBBS, MS (Obstetrics & Gynaecology)', regNo: 'DEMO-REG-0001', council: 'Demo Medical Council', hprId: 'DEMO-HPR-0001' } },
  { id: 'u-s-staff', facilityId: 'sharma-meerut', name: 'Seema Tyagi', email: 'sharma.reception@example.com', phone: '+91 55500 00013', role: 'staff', active: true },
  { id: 'u-g-admin', facilityId: 'gupta-haridwar', name: 'Amit Gupta', email: 'gupta.admin@example.com', phone: '+91 55500 00014', role: 'facility_admin', active: true },
  { id: 'u-g-doc', facilityId: 'gupta-haridwar', name: 'Dr Shalini Gupta', email: 'gupta.doctor@example.com', phone: '+91 55500 00009', role: 'doctor', active: true, title: 'Dr', specialty: 'gynaecologist', doctor: { qualification: 'MBBS, DGO' } },
  { id: 'u-g-staff', facilityId: 'gupta-haridwar', name: 'Deepa Rawat', email: 'gupta.reception@example.com', phone: '+91 55500 00015', role: 'staff', active: true },
];

interface MotherSeed {
  id: string;
  facilityId: string;
  name: string;
  phone: string;
  stage: Stage;
  lmpDaysAgo?: number;
  dobDaysAgo?: number;
  consentDaysAgo: number;
  /** Her own consent (rule 19). yes: how she said it; pending: welcomeDaysAgo = WhatsApp welcome sent. */
  said: ConsentSeed;
  optedOut?: boolean;
  highRisk?: boolean;
  age?: number;
}

// Sample identity / insurance data for the demo only.
const IDENTITY: Record<string, Partial<Mother>> = {
  'm-s1': {
    abha: '00-0000-0000-0001', insurance: { type: 'pmjay', policyId: 'DEMO-PMJAY-0001' },
    address: 'Village Kaili, near Daurala, Meerut',
    family: [{ name: 'Ramesh', relation: 'husband', phone: '+91 55500 00031' }, { name: 'Kamla Devi', relation: 'mother_in_law', phone: '+91 55500 00032' }],
    health: { bloodGroup: 'B+', allergyStatus: 'none', allergies: [], conditions: ['anaemia'], previousPregnancies: 1, previousCSection: false },
  },
  'm-s2': {
    abha: 'demo.mother1@sbx', insurance: { type: 'private', insurer: 'Demo Insurer', policyId: 'DEMO-POLICY-0002' },
    address: 'Shastri Nagar, Meerut',
    family: [{ name: 'Rahul Sharma', relation: 'husband', phone: '+91 55500 00033' }],
    health: { bloodGroup: 'O+', allergyStatus: 'has', allergies: ['penicillin'], conditions: ['high_bp'], conditionNotes: 'BP high in last pregnancy', conditionNotesHi: 'पिछली प्रेगनेंसी में BP ज़्यादा था', otherMedicines: 'Calcium daily', otherMedicinesHi: 'रोज़ कैल्शियम', previousPregnancies: 1, previousCSection: true },
  },
  // Other demo mothers: allergies not asked yet, to show the "please ask" prompt.
};
const BABY_SEX: Record<string, Baby['sex']> = { 'm-s1': 'female', 'm-s3': 'male' };

const S = 'sharma-meerut';
const G = 'gupta-haridwar';

type ConsentSeed = { status: 'yes'; channel: 'whatsapp' | 'paper' | 'call'; daysAgo: number } | { status: 'pending'; welcomeDaysAgo?: number } | { status: 'no'; daysAgo: number };
const CONSENT_TEXT: Record<'whatsapp' | 'paper' | 'call', string | undefined> = { whatsapp: undefined, paper: 'paper-v1', call: 'call-hi-v1' };

function consentFields(c: ConsentSeed): Partial<Mother> {
  if (c.status === 'pending') return { consentStatus: 'pending', welcomeSentAt: c.welcomeDaysAgo !== undefined ? daysAgoAt(c.welcomeDaysAgo, 11, 30) : undefined };
  if (c.status === 'no') return { consentStatus: 'no', consentAt: daysAgoAt(c.daysAgo, 17, 0), optOutReason: 'mother_asked' };
  return { consentStatus: 'yes', consentChannel: c.channel, consentAt: daysAgoAt(c.daysAgo, 12, 15), consentTextVersion: CONSENT_TEXT[c.channel] };
}

const motherSeeds: MotherSeed[] = [
  { id: 'm-s1', facilityId: S, name: 'Sunita Devi', phone: '+91 55500 00016', stage: 'baby', dobDaysAgo: 42, consentDaysAgo: 41, said: { status: 'yes', channel: 'whatsapp', daysAgo: 41 }, age: 26 },
  { id: 'm-s2', facilityId: S, name: 'Pooja Sharma', phone: '+91 55500 00017', stage: 'pregnancy', lmpDaysAgo: 193, consentDaysAgo: 120, said: { status: 'yes', channel: 'paper', daysAgo: 120 }, highRisk: true, age: 31 },
  { id: 'm-s3', facilityId: S, name: 'Rekha Yadav', phone: '+91 55500 00018', stage: 'baby', dobDaysAgo: 75, consentDaysAgo: 74, said: { status: 'yes', channel: 'whatsapp', daysAgo: 74 }, age: 24 },
  { id: 'm-s4', facilityId: S, name: 'Anjali Verma', phone: '+91 55500 00019', stage: 'baby', dobDaysAgo: 96, consentDaysAgo: 95, said: { status: 'no', daysAgo: 12 }, optedOut: true },
  { id: 'm-s5', facilityId: S, name: 'Kavita Singh', phone: '+91 55500 00020', stage: 'pregnancy', lmpDaysAgo: 82, consentDaysAgo: 20, said: { status: 'yes', channel: 'call', daysAgo: 9 } },
  { id: 'm-g1', facilityId: G, name: 'Meena Rawat', phone: '+91 55500 00021', stage: 'baby', dobDaysAgo: 40, consentDaysAgo: 39, said: { status: 'yes', channel: 'whatsapp', daysAgo: 39 } },
  { id: 'm-g2', facilityId: G, name: 'Priya Negi', phone: '+91 55500 00022', stage: 'pregnancy', lmpDaysAgo: 236, consentDaysAgo: 150, said: { status: 'yes', channel: 'paper', daysAgo: 150 }, highRisk: true },
  { id: 'm-g3', facilityId: G, name: 'Neha Bisht', phone: '+91 55500 00023', stage: 'baby', dobDaysAgo: 100, consentDaysAgo: 99, said: { status: 'yes', channel: 'whatsapp', daysAgo: 98 } },
  { id: 'm-g4', facilityId: G, name: 'Savita Chauhan', phone: '+91 55500 00024', stage: 'baby', dobDaysAgo: 60, consentDaysAgo: 59, said: { status: 'no', daysAgo: 15 }, optedOut: true },
  { id: 'm-g5', facilityId: G, name: 'Ritu Joshi', phone: '+91 55500 00025', stage: 'pregnancy', lmpDaysAgo: 137, consentDaysAgo: 60, said: { status: 'yes', channel: 'whatsapp', daysAgo: 60 } },
  // Registered recently; waiting for her yes on WhatsApp, so no calls yet (synthetic).
  { id: 'm-s6', facilityId: S, name: 'Lata Kumari', phone: '+91 55500 00026', stage: 'pregnancy', lmpDaysAgo: 70, consentDaysAgo: 1, said: { status: 'pending', welcomeDaysAgo: 1 }, age: 23 },
  { id: 'm-g6', facilityId: G, name: 'Geeta Rana', phone: '+91 55500 00027', stage: 'pregnancy', lmpDaysAgo: 110, consentDaysAgo: 2, said: { status: 'pending', welcomeDaysAgo: 2 } },
];

export const mothers: Mother[] = motherSeeds.map((s, i) => ({
  ...IDENTITY[s.id],
  uhid: `${s.facilityId === S ? 'SHM' : 'GMC'}-${String(1001 + i)}`,
  age: s.age,
  id: s.id,
  facilityId: s.facilityId,
  name: s.name,
  phone: s.phone,
  stage: s.stage,
  lmp: s.lmpDaysAgo !== undefined ? dateOffset(-s.lmpDaysAgo) : undefined,
  edd: s.lmpDaysAgo !== undefined ? dateOffset(280 - s.lmpDaysAgo) : undefined,
  consent: { given: true, date: dateOffset(-s.consentDaysAgo) },
  ...consentFields(s.said),
  optedOut: !!s.optedOut,
  highRisk: !!s.highRisk,
}));

export const babies: Baby[] = motherSeeds
  .filter((s) => s.dobDaysAgo !== undefined)
  .map((s) => ({ id: `b-${s.id}`, motherId: s.id, facilityId: s.facilityId, dob: dateOffset(-(s.dobDaysAgo as number)), sex: BABY_SEX[s.id] }));

// "What is it for" lines (starting text; each clinic's doctor approves before Asha reads them on calls).
const IMPORTANCE = 'समय पर टीका लगने से बच्चा सबसे जल्दी सुरक्षित होता है।';
const PENTA = 'पेंटा पाँच बीमारियों से बचाता है: गलघोंटू, काली खाँसी, टिटनेस, हेपेटाइटिस-B और Hib, जिससे निमोनिया और दिमागी बुखार हो सकता है।';
export const DEFAULT_ABOUT: Record<string, string> = {
  'ps-birth': `BCG टीबी से, OPV पोलियो से और हेपेटाइटिस-B लिवर की बीमारी से बचाता है। ${IMPORTANCE}`,
  'ps-6w': `${PENTA} OPV पोलियो से, रोटा बच्चों के गंभीर दस्त से, और PCV निमोनिया से बचाता है। ${IMPORTANCE}`,
  'ps-10w': `${PENTA} OPV पोलियो से और रोटा बच्चों के गंभीर दस्त से बचाता है। ${IMPORTANCE}`,
  'ps-14w': `${PENTA} OPV पोलियो से, रोटा बच्चों के गंभीर दस्त से, और PCV निमोनिया से बचाता है। ${IMPORTANCE}`,
  'ps-9m': `MR टीका खसरा और रूबेला से बचाता है, और PCV बूस्टर निमोनिया से सुरक्षा पक्की करता है। ${IMPORTANCE}`,
  'pp-12': 'इस जाँच में आपका और बच्चे का स्वास्थ्य देखा जाता है, जैसे BP, ख़ून की कमी और बच्चे की बढ़त, ताकि कोई परेशानी समय पर पकड़ में आ सके।',
  'pp-20': 'इस जाँच में आपका और बच्चे का स्वास्थ्य देखा जाता है, जैसे BP, ख़ून की कमी और बच्चे की बढ़त, ताकि कोई परेशानी समय पर पकड़ में आ सके।',
  'pp-28': 'इस जाँच में आपका और बच्चे का स्वास्थ्य देखा जाता है, जैसे BP, ख़ून की कमी और बच्चे की बढ़त, ताकि कोई परेशानी समय पर पकड़ में आ सके।',
  'pp-34': 'इस जाँच में डिलीवरी की तैयारी और बच्चे की स्थिति देखी जाती है, ताकि आप और बच्चा सुरक्षित रहें।',
};
/** Fill in the starting "what is it for" line where a step has none yet. */
export const withAbout = <T extends { id: string; aboutHi?: string }>(s: T): T => (s.aboutHi || !DEFAULT_ABOUT[s.id] ? s : { ...s, aboutHi: DEFAULT_ABOUT[s.id] });

export const defaultProtocol: ProtocolStep[] = ([
  { id: 'ps-birth', facilityId: 'default', stage: 'baby', name: 'BCG, OPV-0, Hep B (birth)', ageWeeks: 0, reminderDaysBefore: 0, onTimeWindowDays: 7 },
  { id: 'ps-6w', facilityId: 'default', stage: 'baby', name: 'Penta-1, OPV-1, Rota-1, PCV-1 (6 weeks)', ageWeeks: 6, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'ps-10w', facilityId: 'default', stage: 'baby', name: 'Penta-2, OPV-2, Rota-2 (10 weeks)', ageWeeks: 10, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'ps-14w', facilityId: 'default', stage: 'baby', name: 'Penta-3, OPV-3, Rota-3, PCV-2 (14 weeks)', ageWeeks: 14, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'ps-9m', facilityId: 'default', stage: 'baby', name: 'MR-1, PCV booster (9 months)', ageWeeks: 39, reminderDaysBefore: 3, onTimeWindowDays: 14 },
  { id: 'pp-12', facilityId: 'default', stage: 'pregnancy', name: 'ANC check-up 1 (week 12)', pregnancyWeek: 12, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'pp-20', facilityId: 'default', stage: 'pregnancy', name: 'ANC check-up 2 (week 20)', pregnancyWeek: 20, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'pp-28', facilityId: 'default', stage: 'pregnancy', name: 'ANC check-up 3 (week 28)', pregnancyWeek: 28, reminderDaysBefore: 2, onTimeWindowDays: 7 },
  { id: 'pp-34', facilityId: 'default', stage: 'pregnancy', name: 'ANC check-up 4 (week 34)', pregnancyWeek: 34, reminderDaysBefore: 2, onTimeWindowDays: 7 },
] as ProtocolStep[]).map(withAbout)

const missedOverrides: Record<string, string> = { 'b-m-s3': 'ps-10w', 'b-m-g4': 'ps-6w' };

export function dosesForBaby(b: Baby): Dose[] {
  return defaultProtocol
    .filter((p) => p.stage === 'baby')
    .map((step) => {
      const due = addDays(parseDate(b.dob), (step.ageWeeks ?? 0) * 7);
      let status: DoseStatus = statusByDiff(dayDiff(today, due));
      if (missedOverrides[b.id] === step.id) status = 'missed';
      // Synthetic: most given on the due date, every third one 5 days late (to show recovery after a reminder).
      const late = (step.ageWeeks ?? 0) % 3 === 1 ? 5 : 0;
      const given = status === 'attended' ? toIsoDate(addDays(due, late)) : undefined;
      return { id: `${b.id}-${step.id}`, babyId: b.id, facilityId: b.facilityId, name: step.name, dueDate: toIsoDate(due), status, code: VACCINE_CODES[step.id], ...(given ? { attendedAt: given } : {}) };
    });
}

export const doses: Dose[] = babies.flatMap(dosesForBaby);

// Attendance marks for pregnancy check-ups, keyed `${motherId}-${stepId}`.
export const milestoneMarks: Record<string, DoseStatus> = {};

const call = (
  id: string,
  facilityId: string,
  motherId: string,
  agent: AgentType,
  daysAgo: number,
  time: [number, number],
  durationSec: number,
  outcome: CallOutcome,
  summary: string,
  summaryHi?: string,
  transcript?: string,
): Call => ({ id, facilityId, motherId, agent, startedAt: daysAgoAt(daysAgo, time[0], time[1]), durationSec, outcome, summary, summaryHi, transcript });

export const calls: Call[] = [
  call('c-s1', S, 'm-s1', 'baby', 0, [10, 15], 124, 'confirmed', 'Confirmed visit today for 6-week vaccines; will come by 12 noon.', 'आज 6 हफ़्ते वाले टीकों के लिए आना पक्का किया; दोपहर 12 बजे तक आएँगी।',
    'एजेंट: नमस्ते, मैं शर्मा हॉस्पिटल से, डॉ. राजेश कुमार की तरफ़ से बोल रही हूँ। आज बच्चे के 6 हफ़्ते वाले टीके हैं।\nमाँ: हाँ जी, हम 12 बजे तक आ जाएँगे।\nएजेंट: बहुत अच्छा। टीकाकरण कार्ड साथ लाना न भूलें। धन्यवाद!'),
  call('c-s2', S, 'm-s2', 'pregnancy', 0, [10, 42], 211, 'question_flagged', 'Mother reported swelling in feet and headache since last night; asked to speak to the doctor. Flag raised.', 'माँ ने बताया कि कल रात से पैरों में सूजन और सिरदर्द है; डॉक्टर से बात करना चाहती हैं। फ़्लैग बनाया गया।'),
  call('c-s3', S, 'm-s5', 'pregnancy', 0, [11, 5], 0, 'failed', 'Call could not connect (network busy).', 'कॉल नहीं लगी (नेटवर्क व्यस्त था)।'),
  call('c-s4', S, 'm-s3', 'baby', 1, [11, 20], 0, 'failed', 'Call could not connect (switched off).', 'कॉल नहीं लगी (फ़ोन बंद था)।'),
  call('c-s5', S, 'm-s3', 'baby', 6, [10, 30], 0, 'no_answer', 'Rang 6 times, no answer. Retry scheduled.', '6 बार घंटी गई, किसी ने नहीं उठाया। दोबारा कॉल तय की गई है।'),
  call('c-s6', S, 'm-s1', 'baby', 3, [12, 10], 156, 'question_flagged', 'Asked whether the baby can be bathed on vaccination day. Passed to clinic.', 'पूछा कि क्या टीके वाले दिन बच्चे को नहला सकते हैं। सवाल क्लिनिक को भेज दिया।'),
  call('c-s7', S, 'm-s4', 'baby', 12, [16, 45], 48, 'opted_out', 'Mother asked to stop calls; family moved to Ghaziabad.', 'माँ ने कॉल बंद करने को कहा; परिवार गाज़ियाबाद चला गया है।'),
  call('c-s8', S, 'm-s5', 'pregnancy', 9, [17, 5], 132, 'confirmed', 'Welcome call; mother confirmed she is happy to get reminders.', 'स्वागत कॉल; माँ ने कहा कि उन्हें रिमाइंडर मिलना ठीक है।'),
  call('c-s9', S, 'm-s2', 'pregnancy', 24, [11, 0], 167, 'confirmed', 'Reminder for ANC check-up; confirmed visit.', 'ANC जाँच का रिमाइंडर; आना पक्का किया।'),
  call('c-g1', G, 'm-g1', 'baby', 0, [10, 5], 98, 'confirmed', 'Reminded about 6-week vaccines in 2 days; mother confirmed.', '2 दिन बाद 6 हफ़्ते वाले टीकों की याद दिलाई; माँ ने आना पक्का किया।'),
  call('c-g2', G, 'm-g2', 'pregnancy', 0, [10, 30], 186, 'question_flagged', 'Mother said baby movements feel less since morning; wants to talk to the doctor. Flag raised.', 'माँ ने बताया कि सुबह से बच्चे की हलचल कम लग रही है; डॉक्टर से बात करना चाहती हैं। फ़्लैग बनाया गया।'),
  call('c-g3', G, 'm-g5', 'pregnancy', 0, [11, 15], 0, 'failed', 'Call could not connect (number busy).', 'कॉल नहीं लगी (नंबर व्यस्त था)।'),
  call('c-g4', G, 'm-g3', 'baby', 1, [12, 40], 0, 'no_answer', 'Rang 6 times, no answer. Retry scheduled for today.', '6 बार घंटी गई, किसी ने नहीं उठाया। आज दोबारा कॉल तय है।'),
  call('c-g5', G, 'm-g3', 'baby', 4, [10, 50], 110, 'confirmed', 'Confirmed 14-week vaccine visit.', '14 हफ़्ते वाले टीके के लिए आना पक्का किया।'),
  call('c-g6', G, 'm-g4', 'baby', 15, [15, 30], 39, 'opted_out', 'Mother said she will go to the government centre; asked to stop calls.', 'माँ ने कहा कि वे सरकारी केंद्र जाएँगी; कॉल बंद करने को कहा।'),
  call('c-g7', G, 'm-g2', 'pregnancy', 10, [11, 25], 143, 'question_flagged', 'Asked if she can travel to Dehradun by bus next week. Passed to doctor.', 'पूछा कि क्या अगले हफ़्ते बस से देहरादून जा सकती हैं। सवाल डॉक्टर को भेज दिया।'),
  call('c-g8', G, 'm-g5', 'pregnancy', 6, [14, 0], 121, 'question_flagged', 'Requested a callback about the diet chart given at the last visit.', 'पिछली बार दिए गए डाइट चार्ट के बारे में वापस कॉल माँगी।'),
];

// Synthetic Hindi transcripts for every answered demo call (failed or unanswered calls have none).
// The AI always says it is an AI and the call is recorded, gives no medical advice, and passes questions to the clinic.
const TRANSCRIPTS: Record<string, string> = demoConversations.calls;
calls.forEach((c) => { if (TRANSCRIPTS[c.id]) c.transcript = TRANSCRIPTS[c.id]; });

// Synthetic history: a reminder call the day after each late vaccine was due (the dose was then given),
// so the demo's impact report has "doses recovered" to show.
doses
  .filter((d) => d.attendedAt && d.attendedAt > d.dueDate)
  .forEach((d, n) => {
    const b = babies.find((x) => x.id === d.babyId);
    const daysAgo = -dayDiff(today, addDays(parseDate(d.dueDate), 1));
    if (b && daysAgo > 0) calls.push(call(`c-r${n}`, d.facilityId, b.motherId, 'baby', daysAgo, [10, 0], 95, 'confirmed', `Reminder: ${d.name} was due yesterday; mother confirmed she will come this week.`,
      `याद दिलाया: ${d.name} कल लगना था; माँ ने कहा इसी हफ़्ते आएँगी।`,
      demoConversations.calls.reminder));
  });

export const flags: Flag[] = [
  { id: 'f-s1', facilityId: S, motherId: 'm-s2', callId: 'c-s2', dangerWords: ['सूजन', 'severe headache'], motherWords: ['कल रात से पैरों में बहुत सूजन है और सिर में तेज़ दर्द है / Since last night my feet are very swollen and I have a severe headache'], reason: 'Swelling in feet and headache since last night; wants to speak to the doctor.', reasonHi: 'कल रात से पैरों में सूजन और सिरदर्द; डॉक्टर से बात करना चाहती हैं।', urgency: 'urgent', status: 'open', assignedTo: 'Dr Anita Sharma', raisedAt: minutesAgo(47) },
  { id: 'f-s2', facilityId: S, motherId: 'm-s1', callId: 'c-s6', reason: 'Asked whether the baby can be bathed on vaccination day.', reasonHi: 'पूछा कि क्या टीके वाले दिन बच्चे को नहला सकते हैं।', urgency: 'normal', status: 'in_progress', assignedTo: 'Seema Tyagi', raisedAt: daysAgoAt(3, 12, 13), acknowledgedAt: daysAgoAt(3, 14, 0), acknowledgedBy: 'Seema Tyagi' },
  { id: 'f-s3', facilityId: S, motherId: 'm-s4', dangerWords: ['not waking for feeds'], reason: 'Baby very sleepy since morning and not waking for feeds (the mother said "yes" on the check-in call)', reasonHi: 'बच्चा सुबह से बहुत सुस्त है और दूध के लिए नहीं उठ रहा (चेक-इन कॉल पर माँ ने "हाँ" कहा)', urgency: 'urgent', status: 'resolved', assignedTo: 'Dr Anita Sharma', raisedAt: minutesAgo(200), acknowledgedAt: minutesAgo(150), acknowledgedBy: 'Dr Anita Sharma', resolvedNote: 'Spoke to the mother: baby awake and feeding now. Seeing her at today\'s visit.', resolvedNoteHi: 'माँ से बात की: बच्चा अब जागा है और दूध पी रहा है। आज की विज़िट में देखेंगे।', resolvedByName: 'Dr Anita Sharma', resolvedAt: minutesAgo(140) },
  { id: 'f-g1', facilityId: G, motherId: 'm-g2', callId: 'c-g2', dangerWords: ['हलचल कम'], motherWords: ['सुबह से बच्चे की हलचल कम लग रही है / Since morning the baby\'s movements feel less'], reason: 'Baby movements feel less since morning; wants to talk to the doctor.', reasonHi: 'सुबह से बच्चे की हलचल कम लग रही है; डॉक्टर से बात करना चाहती हैं।', urgency: 'urgent', status: 'open', assignedTo: 'Dr Shalini Gupta', raisedAt: minutesAgo(95) },
  { id: 'f-g2', facilityId: G, motherId: 'm-g5', callId: 'c-g8', reason: 'Requested a callback about the diet chart from the last visit.', reasonHi: 'पिछली बार के डाइट चार्ट के बारे में वापस कॉल माँगी।', urgency: 'normal', status: 'open', assignedTo: 'Deepa Rawat', raisedAt: daysAgoAt(6, 14, 3) },
  { id: 'f-g3', facilityId: G, motherId: 'm-g2', callId: 'c-g7', reason: 'Asked about travelling by bus next week.', reasonHi: 'अगले हफ़्ते बस से सफ़र के बारे में पूछा।', urgency: 'normal', status: 'resolved', assignedTo: 'Dr Shalini Gupta', raisedAt: daysAgoAt(10, 11, 28), resolvedNote: 'Doctor called back and discussed in person.', resolvedNoteHi: 'डॉक्टर ने वापस कॉल किया और आमने-सामने बात की।' },
];

export const COST_PER_MINUTE = 4;

function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function buildStats(facilityId: string, seed: number, base: { enrolled: number; calls: number; answer: number; onTime: number }): DailyStats[] {
  const rnd = seeded(seed);
  return Array.from({ length: 30 }, (_, i) => {
    const daysAgo = 29 - i;
    const callsMade = Math.round(base.calls + rnd() * 10);
    const callsAnswered = Math.round(callsMade * (base.answer + rnd() * 0.12));
    const callMinutes = Math.round(callsAnswered * (1.6 + rnd() * 0.8));
    const whatsappSent = Math.round(callsMade * 1.3);
    return {
      facilityId,
      date: dateOffset(-daysAgo),
      patientsEnrolled: base.enrolled + Math.floor(i * 0.8),
      callsMade,
      callsAnswered,
      callsFailed: Math.round(1 + rnd() * 3),
      whatsappSent,
      whatsappRead: Math.round(whatsappSent * (0.62 + rnd() * 0.2)),
      onTimeRate: Math.round((base.onTime + i * 0.15 + rnd() * 3) * 10) / 10,
      openEscalations: daysAgo === 0 ? 2 : Math.round(rnd() * 3),
      callMinutes,
      estimatedCost: callMinutes * COST_PER_MINUTE,
    };
  });
}

export const dailyStats: DailyStats[] = [
  ...buildStats(S, 7, { enrolled: 138, calls: 20, answer: 0.58, onTime: 76 }),
  ...buildStats(G, 13, { enrolled: 84, calls: 12, answer: 0.4, onTime: 68 }),
];

export const agents: Agent[] = [
  { id: 'agent-baby', type: 'baby', coreRulesVersion: 'core-1.2', scriptVersion: 'baby-2.0', status: 'approved', approvedBy: 'Dr Rajesh Kumar', approvedAt: dateOffset(-20), languages: ['Hindi', 'Hinglish'], voiceProvider: 'Sarvam', lastTestAt: dateOffset(-2) },
  { id: 'agent-pregnancy', type: 'pregnancy', coreRulesVersion: 'core-1.2', scriptVersion: 'preg-0.9', status: 'awaiting_signoff', languages: ['Hindi'], voiceProvider: 'Sarvam', lastTestAt: dateOffset(-9) },
];

export const scriptContent: Record<string, { scriptHi: string; faqs: { q: string; a: string }[] }> = {
  'agent-baby': {
    scriptHi: 'नमस्ते, मैं {clinic} से, {doctor} की तरफ़ से बोल रही हूँ।\nआपके बच्चे का अगला टीका {date} को है। क्या आप उस दिन क्लिनिक आ पाएँगी?\nअगर हाँ: बहुत अच्छा। टीकाकरण कार्ड साथ लाना न भूलें।\nअगर नहीं: कोई बात नहीं। कृपया क्लिनिक आकर या फ़ोन करके नई तारीख़ तय कर लीजिए। मैंने क्लिनिक के लिए नोट कर लिया है।\nअगर कोई सवाल है: कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।\nकॉल बंद करवाने के लिए कभी भी "बंद करो" कहें। धन्यवाद!',
    faqs: [
      { q: 'क्या टीके के दिन बच्चे को नहला सकते हैं?', a: 'कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
      { q: 'क्लिनिक कितने बजे खुलता है?', a: 'कृपया अपनी अगली OPD विज़िट पर क्लिनिक में पूछ लीजिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
      { q: 'बच्चे को बुख़ार है, क्या टीका लगवाएँ?', a: 'कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
    ],
  },
  'agent-pregnancy': {
    scriptHi: 'नमस्ते, मैं {clinic} से, {doctor} की तरफ़ से बोल रही हूँ।\nआपकी अगली जाँच {date} को है। क्या आप आ पाएँगी?\nअगर आपको सूजन, सिरदर्द, खून आना या बच्चे की हलचल कम लगे, तो तुरंत क्लिनिक से संपर्क करें।\nकोई सवाल हो तो: कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है। धन्यवाद!',
    faqs: [
      { q: 'क्या मैं बस से सफ़र कर सकती हूँ?', a: 'कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
      { q: 'कौन-सी दवा लूँ?', a: 'दवा के बारे में मैं कुछ नहीं बता सकती। कृपया अपनी अगली OPD विज़िट पर डॉक्टर से पूछिए। मैंने क्लिनिक के लिए यह नोट कर लिया है।' },
    ],
  },
};

export const supportRequests: SupportAccessRequest[] = [
  { id: 'sar-1', facilityId: G, requestedBy: 'Rohit Mehta', requestedAt: daysAgoAt(0, 9, 20), reason: 'Daily job did not run; need to check calling setup.', reasonHi: 'रोज़ का जॉब नहीं चला; कॉलिंग सेटअप देखना है।', status: 'pending' },
  { id: 'sar-2', facilityId: S, requestedBy: 'Rohit Mehta', requestedAt: daysAgoAt(9, 11, 0), reason: 'WhatsApp template not delivering.', reasonHi: 'WhatsApp टेम्पलेट डिलीवर नहीं हो रहा।', status: 'expired', approvedBy: 'Vikas Sharma', expiresAt: daysAgoAt(8, 11, 5) },
  { id: 'sar-3', facilityId: S, requestedBy: 'Rohit Mehta', requestedAt: daysAgoAt(0, 8, 30), reason: 'Checking retry rule after answer-rate drop.', reasonHi: 'कॉल उठाने की दर गिरी है, इसलिए दोबारा कॉल का नियम देख रहे हैं।', status: 'approved', approvedBy: 'Vikas Sharma', expiresAt: daysAgoAt(-1, 8, 45) },
];

export const auditLog: AuditEntry[] = [
  { id: 'a-1', facilityId: S, userId: 'u-super', actor: 'Rohit Mehta', role: 'super_admin', action: 'facility.updated', target: 'Sharma Hospital', detail: 'Calling hours 10:00–19:00', at: daysAgoAt(2, 15, 10) },
  { id: 'a-2', facilityId: G, userId: 'u-super', actor: 'Rohit Mehta', role: 'super_admin', action: 'support_access.requested', target: 'Gupta Maternity & Child Clinic', at: daysAgoAt(0, 9, 20) },
  { id: 'a-3', facilityId: S, userId: 'u-s-admin', actor: 'Vikas Sharma', role: 'facility_admin', action: 'support_access.approved', target: 'Sharma Hospital', detail: '24 hours', at: daysAgoAt(0, 8, 45) },
  { id: 'a-4', facilityId: S, userId: 'u-s-doc', actor: 'Dr Anita Sharma', role: 'doctor', action: 'consent.approved', target: 'Sharma Hospital', at: daysAgoAt(21, 12, 0) },
  { id: 'a-5', facilityId: G, userId: 'u-g-staff', actor: 'Deepa Rawat', role: 'staff', action: 'patient.opted_out', target: 'Patient m-g5', detail: 'mother_asked', at: daysAgoAt(4, 16, 30) },
];

export const platformAlerts: PlatformAlert[] = [
  { id: 'pa-1', severity: 'red', facilityId: G, textEn: "Daily job didn't run at Gupta Maternity & Child Clinic (last run 26 h ago)", textHi: 'गुप्ता मैटरनिटी एवं चाइल्ड क्लिनिक में दैनिक जॉब नहीं चला (आख़िरी बार 26 घंटे पहले)', at: daysAgoAt(0, 8, 0) },
  { id: 'pa-2', severity: 'amber', facilityId: G, textEn: 'Answer rate below 50% at Gupta Maternity & Child Clinic (last 7 days)', textHi: 'गुप्ता मैटरनिटी एवं चाइल्ड क्लिनिक में उत्तर दर 50% से कम (पिछले 7 दिन)', at: daysAgoAt(0, 7, 30) },
  { id: 'pa-3', severity: 'amber', textEn: 'Telephony credit low: ₹1,250 left (about 4 days)', textHi: 'टेलीफ़ोनी क्रेडिट कम: ₹1,250 बाकी (लगभग 4 दिन)', at: daysAgoAt(0, 7, 0) },
];

facilities[0].signoffs = { script: { by: 'Dr Rajesh Kumar', at: dateOffset(-20) }, consent: { by: 'Dr Anita Sharma', at: dateOffset(-21) } };
facilities[1].signoffs = { script: { by: 'Dr Manoj Gupta', at: dateOffset(-20) }, consent: { by: 'Dr Shalini Gupta', at: dateOffset(-18) } };

// Each clinic's own copy of the schedule, created from defaultProtocol.
export const clinicProtocols: Record<string, ProtocolStep[]> = Object.fromEntries(
  facilities.map((f) => [f.id, defaultProtocol.map((s) => ({ ...s, facilityId: f.id }))]),
);

export const serviceHealth: ServiceHealth[] = [
  { key: 'dailyJob', level: 'red', lastCheck: daysAgoAt(0, 8, 0), detail: 'sharma-meerut ran 07:00 OK; gupta-haridwar last run 26 h ago (lock timeout)' },
  { key: 'voice', level: 'green', lastCheck: minutesAgo(2), detail: 'Sarvam TTS/STT p95 latency 420 ms' },
  { key: 'telephony', level: 'amber', lastCheck: minutesAgo(5), detail: 'Credit ₹1,250 (~4 days); SIP error rate 2.1%' },
  { key: 'whatsapp', level: 'green', lastCheck: minutesAgo(3), detail: 'Templates approved: 3/3; delivery 97%' },
  { key: 'webhook', level: 'amber', lastCheck: minutesAgo(1), detail: '2 retries in last hour; queue depth 4' },
  { key: 'database', level: 'green', lastCheck: minutesAgo(1), detail: 'Read p95 38 ms; storage 12% used' },
];

export const errorLog: ErrorLogEntry[] = [
  { id: 'e-1', at: daysAgoAt(0, 7, 0), service: 'dailyJob', code: 'JOB_LOCK_TIMEOUT', message: 'scheduler: facility=gupta-haridwar run skipped, lock not acquired after 30s' },
  { id: 'e-2', at: minutesAgo(38), service: 'webhook', code: 'HTTP_502', message: 'POST /webhooks/voice/status upstream 502, retry 2/3' },
  { id: 'e-3', at: minutesAgo(64), service: 'telephony', code: 'SIP_486', message: 'call_id=c_8f21a busy here, retry scheduled +2h' },
  { id: 'e-4', at: daysAgoAt(1, 18, 12), service: 'whatsapp', code: 'WA_131056', message: 'template=dose_reminder_v2 pair rate limit hit, backoff 60s' },
  { id: 'e-5', at: daysAgoAt(2, 10, 5), service: 'voice', code: 'STT_TIMEOUT', message: 'stream=s_41c0 no final transcript in 8s, fell back to DTMF' },
];

export const topQuestions: Record<string, { topic: string; topicHi?: string; count: number }[]> = {
  [S]: [{ topic: 'Clinic timings', topicHi: 'क्लिनिक का समय', count: 18 }, { topic: 'Fever after vaccine', topicHi: 'टीके के बाद बुखार', count: 11 }, { topic: 'Reschedule visit', topicHi: 'विज़िट की तारीख़ बदलनी है', count: 9 }, { topic: 'Bring vaccination card?', topicHi: 'टीकाकरण कार्ड लाना है?', count: 6 }],
  [G]: [{ topic: 'Reschedule visit', topicHi: 'विज़िट की तारीख़ बदलनी है', count: 12 }, { topic: 'Clinic timings', topicHi: 'क्लिनिक का समय', count: 8 }, { topic: 'Travel during pregnancy', topicHi: 'प्रेगनेंसी में सफ़र', count: 4 }],
};

// Visit time slots (synthetic). Both demo clinics have slots switched on: Mon–Sat, three morning slots.
const demoPlan = () => ({ days: [1, 2, 3, 4, 5, 6], slots: [{ start: '10:00', end: '11:00', capacity: 6 }, { start: '11:00', end: '12:00', capacity: 6 }, { start: '12:00', end: '13:00', capacity: 6 }] });
export const slotSettings: Record<string, SlotSettings> = {
  [S]: { enabled: true, baby: demoPlan(), pregnancy: demoPlan(), closedDates: [], updatedByName: 'Seema Tyagi', updatedAt: daysAgoAt(5, 11, 0) },
  [G]: { enabled: true, baby: demoPlan(), pregnancy: demoPlan(), closedDates: [], updatedByName: 'Deepa Rawat', updatedAt: daysAgoAt(4, 12, 0) },
};

// Next clinic day after today (tomorrow, or Monday when tomorrow is Sunday).
const nextVisitDay = (() => { let n = 1; while (addDays(today, n).getDay() === 0) n += 1; return dateOffset(n); })();

// Each demo mother's next not-attended dose / check-up, booked for the next clinic day.
function nextItem(motherId: string): { itemId: string; itemName: string; agent: 'baby' | 'pregnancy' } | undefined {
  const d = doses.find((x) => x.babyId === `b-${motherId}` && x.status !== 'attended');
  if (d) return { itemId: d.id, itemName: d.name, agent: 'baby' };
  const m = mothers.find((x) => x.id === motherId);
  if (!m?.lmp) return undefined;
  const step = defaultProtocol.find((p) => p.stage === 'pregnancy' && dayDiff(today, addDays(parseDate(m.lmp!), (p.pregnancyWeek ?? 0) * 7)) >= -3);
  return step && { itemId: `${motherId}-${step.id}`, itemName: step.name, agent: 'pregnancy' };
}

// Demo bookings today sit around the current hour, so Today shows one past and one coming visit at any time of day.
function hourIst(offset: number): string {
  const nowIst = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }).format(new Date())) % 24;
  // Kept inside the demo clinic's visit slots (10:00–17:00), never at night.
  const h = Math.min(17, Math.max(10, nowIst + offset));
  return `${String(h).padStart(2, '0')}:00`;
}
// The last value 'today' books for today instead of the next clinic day (so Today shows bookings).
const demoBookings: [string, string, string, Booking['source'], number, ('today' | undefined)?][] = [
  ['m-s1', hourIst(-2), hourIst(-1), 'call', 2, 'today'],
  ['m-s4', hourIst(1), hourIst(2), 'reception', 1, 'today'],
  ['m-s2', '11:00', '12:00', 'call', 3],
  ['m-s5', '12:00', '13:00', 'call', 1],
  ['m-g1', '10:00', '11:00', 'call', 2],
  ['m-g3', '11:00', '12:00', 'call', 1],
  ['m-g5', '10:00', '11:00', 'call', 2],
];

export const bookings: Booking[] = demoBookings.flatMap(([motherId, start, end, source, hoursAgo, when]) => {
  const it = nextItem(motherId);
  const m = mothers.find((x) => x.id === motherId)!;
  if (!it) return [];
  return [{
    facilityId: m.facilityId, agent: it.agent, motherId, itemId: it.itemId, itemName: it.itemName, date: when === 'today' ? dateOffset(0) : nextVisitDay, start, end, status: 'booked' as const, source,
    ...(source === 'call' ? {} : { bookedByName: m.facilityId === S ? 'Seema Tyagi' : 'Deepa Rawat' }),
    bookedAt: minutesAgo(hoursAgo * 60),
  }];
});
